import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Header } from '../components/Header';
import { PageHeader } from '../components/PageHeader';
import { PageLayout } from '../components/PageLayout';
import { authenticatedFetch } from '../utils/fetchHelper';
import { useSocket } from '../contexts/SocketContext';
import toast from 'react-hot-toast';

interface OverviewData {
  period: string;
  messages: { sent: number; limit: number };
  delivery: { sent: number; failed: number; ratePercent: number };
  connections: { active: number; total: number };
  activeCampaignsCount?: number;
}

interface CampaignItem {
  id: string;
  nome: string;
  status: string;
  criadoEm: string;
  sentCount: number;
  failedCount: number;
  totalContacts: number;
}

/**
 * Dashboard do tenant: overview do mês atual + últimas campanhas.
 * Dados sempre do tenant do usuário (SaaS).
 */
export function DashboardPage() {
  const [overview, setOverview] = useState<OverviewData | null>(null);
  const [campaigns, setCampaigns] = useState<CampaignItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [overviewRes, campaignsRes] = await Promise.all([
          authenticatedFetch('/api/dashboard/overview'),
          authenticatedFetch('/api/campaigns?limit=10&page=1'),
        ]);
        if (overviewRes.ok) {
          const json = await overviewRes.json();
          if (json.success && json.data) setOverview(json.data);
        }
        if (campaignsRes.ok) {
          const json = await campaignsRes.json();
          setCampaigns(json.campaigns || []);
        }
      } catch (e) {
        console.error(e);
        toast.error('Erro ao carregar o dashboard');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const lastOverviewRefetchRef = useRef<number>(0);
  const OVERVIEW_REFETCH_THROTTLE_MS = 2000;

  const refetchOverview = async () => {
    const now = Date.now();
    if (now - lastOverviewRefetchRef.current < OVERVIEW_REFETCH_THROTTLE_MS) return;
    lastOverviewRefetchRef.current = now;
    try {
      const res = await authenticatedFetch('/api/dashboard/overview');
      if (res.ok) {
        const json = await res.json();
        if (json.success && json.data) setOverview(json.data);
      }
    } catch {
      // silencioso
    }
  };

  // WebSocket: usar conexão compartilhada (SocketContext); só inscrever em new_notification
  const socket = useSocket();
  useEffect(() => {
    if (!socket) return;
    const handler = (notification: { type: string; data?: any }) => {
      if (notification.type === 'CAMPAIGN_PROGRESS' && notification.data) {
        const { campaignId, sentCount, failedCount, status } = notification.data;
        setCampaigns((prev) =>
          prev.map((c) =>
            c.id === campaignId
              ? {
                  ...c,
                  ...(sentCount !== undefined && { sentCount }),
                  ...(failedCount !== undefined && { failedCount }),
                  ...(status && { status }),
                }
              : c
          )
        );
        refetchOverview();
        window.dispatchEvent(new CustomEvent('quota-updated'));
      }
      if (notification.type === 'SUCCESS' && notification.data?.campaignId && notification.data?.status === 'COMPLETED') {
        const { campaignId, sentCount, failedCount, totalContacts } = notification.data;
        setCampaigns((prev) =>
          prev.map((c) =>
            c.id === campaignId
              ? {
                  ...c,
                  status: 'COMPLETED',
                  ...(sentCount !== undefined && { sentCount }),
                  ...(failedCount !== undefined && { failedCount }),
                  ...(totalContacts !== undefined && { totalContacts }),
                }
              : c
          )
        );
        refetchOverview();
        window.dispatchEvent(new CustomEvent('quota-updated'));
      }
    };
    socket.on('new_notification', handler);
    return () => {
      socket.off('new_notification', handler);
    };
  }, [socket]);

  const formatDate = (s: string) => {
    try {
      return new Date(s).toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return s;
    }
  };

  const statusLabel: Record<string, string> = {
    PENDING: 'Pendente',
    RUNNING: 'Em envio',
    COMPLETED: 'Concluída',
    CANCELLED: 'Cancelada',
    PAUSED: 'Pausada',
  };

  return (
    <>
      <Header title="Dashboard" subtitle="Visão geral da sua conta" />
      <PageLayout>
        <PageHeader
          title="Dashboard"
          breadcrumbs={[
            { label: 'Home', href: '/dashboard' },
            { label: 'Dashboard' },
          ]}
        />
        <div className="space-y-8 w-full">
          {/* Overview card - padrão TailAdmin */}
          <div className="mb-6 rounded-2xl border border-gray-200 bg-white p-4 sm:p-6 dark:border-gray-800 dark:bg-white/[0.03]">
            <div className="mb-6 flex items-center justify-between">
              <h2 className="font-semibold text-gray-800 dark:text-white/90">Visão do mês</h2>
              <span className="text-sm text-gray-500 dark:text-gray-400">
                {overview?.period ?? new Date().toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })}
              </span>
            </div>
            {loading ? (
              <div className="grid grid-cols-1 rounded-xl border border-gray-200 sm:grid-cols-2 lg:grid-cols-4 dark:border-gray-800">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="border-b p-5 sm:border-r lg:border-b-0 animate-pulse">
                    <div className="mb-1.5 h-4 w-24 bg-gray-200 rounded dark:bg-gray-700" />
                    <div className="h-8 w-16 bg-gray-200 rounded dark:bg-gray-700" />
                  </div>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 rounded-xl border border-gray-200 sm:grid-cols-2 lg:grid-cols-4 lg:divide-x lg:divide-y-0 dark:divide-gray-800 dark:border-gray-800">
                <div className="border-b p-5 sm:border-r lg:border-b-0">
                  <div className="mb-1.5 flex items-center gap-2">
                    <span className="text-blue-600 dark:text-blue-400 text-xl" aria-hidden>
                      <i className="fas fa-envelope" />
                    </span>
                    <p className="text-sm text-gray-400 dark:text-gray-500">Mensagens no mês</p>
                  </div>
                  <h3 className="text-3xl text-gray-800 dark:text-white/90">
                    {overview
                      ? overview.messages.limit === 0
                        ? `${overview.messages.sent.toLocaleString('pt-BR')} enviadas`
                        : `${overview.messages.sent.toLocaleString('pt-BR')} / ${overview.messages.limit.toLocaleString('pt-BR')}`
                      : '–'}
                  </h3>
                </div>
                <div className="border-b p-5 lg:border-b-0">
                  <div className="mb-1.5 flex items-center gap-2">
                    <span className="text-green-600 dark:text-green-400 text-xl" aria-hidden>
                      <i className="fas fa-circle-check" />
                    </span>
                    <p className="text-sm text-gray-400 dark:text-gray-500">Taxa de entrega</p>
                  </div>
                  <h3 className="text-3xl text-gray-800 dark:text-white/90">
                    {overview ? `${overview.delivery.ratePercent}%` : '–'}
                  </h3>
                  {overview && (overview.delivery.sent > 0 || overview.delivery.failed > 0) && (
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      {overview.delivery.sent.toLocaleString('pt-BR')} enviadas, {overview.delivery.failed.toLocaleString('pt-BR')} falhas
                    </p>
                  )}
                </div>
                <div className="border-b p-5 sm:border-r sm:border-b-0">
                  <div className="mb-1.5 flex items-center gap-2">
                    <span className="text-teal-600 dark:text-teal-400 text-xl" aria-hidden>
                      <i className="fas fa-phone" />
                    </span>
                    <p className="text-sm text-gray-400 dark:text-gray-500">Números ativos</p>
                  </div>
                  <h3 className="text-3xl text-gray-800 dark:text-white/90">
                    {overview ? `${overview.connections.active}/${overview.connections.total} ativos` : '–'}
                  </h3>
                </div>
                <div className="p-5">
                  <div className="mb-1.5 flex items-center gap-2">
                    <span className="text-amber-600 dark:text-amber-400 text-xl" aria-hidden>
                      <i className="fas fa-bullhorn" />
                    </span>
                    <p className="text-sm text-gray-400 dark:text-gray-500">Campanhas ativas</p>
                  </div>
                  <h3 className="text-3xl text-gray-800 dark:text-white/90">
                    {overview?.activeCampaignsCount ?? 0}
                  </h3>
                </div>
              </div>
            )}
          </div>

          {/* Últimas campanhas */}
          <div className="card-padrao overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="font-semibold text-gray-800 dark:text-white/90">Últimas campanhas</h2>
            </div>
            <div className="overflow-x-auto">
              {loading ? (
                <div className="p-6 animate-pulse space-y-3">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="h-10 bg-gray-100 dark:bg-gray-800 rounded" />
                  ))}
                </div>
              ) : campaigns.length === 0 ? (
                <div className="p-6 text-center text-gray-500 dark:text-gray-400">
                  Nenhuma campanha encontrada.
                </div>
              ) : (
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-800/50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Nome</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Data</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Enviadas</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Falhas</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Ação</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-white/[0.02] divide-y divide-gray-200 dark:divide-gray-700">
                    {campaigns.map((c) => (
                      <tr key={c.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/30">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white/90">{c.nome}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">{formatDate(c.criadoEm)}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">{c.sentCount ?? 0}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">{c.failedCount ?? 0}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                              c.status === 'COMPLETED'
                                ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                                : c.status === 'RUNNING'
                                ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'
                                : c.status === 'CANCELLED' || c.status === 'PAUSED'
                                ? 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                                : 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400'
                            }`}
                          >
                            {statusLabel[c.status] ?? c.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                          <Link
                            to="/campanhas"
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/30 font-medium transition-colors"
                            title="Ver campanhas"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                            Ver
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      </PageLayout>
    </>
  );
}
